package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.DashboardDetail
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.time.Clock

enum class IncomeVsExpensesTimeRange(val displayName: String, val months: Int) {
    SixMonths("6M", 6),
    TwelveMonths("1Y", 12),
    TwoYears("2Y", 24)
}

sealed class IncomeVsExpensesSummaryScreenState {
    data object Loading : IncomeVsExpensesSummaryScreenState()
    data class Error(val message: String) : IncomeVsExpensesSummaryScreenState()
    data class Loaded(
        val monthlyData: List<MonthlyIncomeVsExpenses>,
        val totalIncome: Double,
        val totalExpenses: Double,
        val netIncome: Double,
        val savingsRate: Double,
        val averageMonthlyIncome: Double,
        val averageMonthlyExpenses: Double,
        val averageMonthlyNet: Double,
        val selectedRange: IncomeVsExpensesTimeRange = IncomeVsExpensesTimeRange.TwelveMonths
    ) : IncomeVsExpensesSummaryScreenState()
}

@OptIn(ExperimentalCoroutinesApi::class)
class IncomeVsExpensesSummaryViewModel(
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val dashboardUseCase: DashboardUseCase
) : ViewModel() {

    private val _selectedRange = MutableStateFlow(IncomeVsExpensesTimeRange.TwelveMonths)
    private val _state = MutableStateFlow<IncomeVsExpensesSummaryScreenState>(IncomeVsExpensesSummaryScreenState.Loading)
    val state: StateFlow<IncomeVsExpensesSummaryScreenState> = _state.asStateFlow()

    init {
        startFlowCollection()
    }

    fun setTimeRange(range: IncomeVsExpensesTimeRange) {
        _selectedRange.value = range
    }

    fun refresh() {
        viewModelScope.launch {
            transactionRepository.refresh()
        }
    }

    private fun startFlowCollection() {
        viewModelScope.launch {
            val dashboardsDetailsFlow = dashboardUseCase.GetDashboards().flatMapLatest { dashboardsResponse ->
                when (dashboardsResponse) {
                    is Response.Error -> flowOf(Response.Error(dashboardsResponse.message))
                    Response.Loading -> flowOf(Response.Loading)
                    is Response.Success -> {
                        val dashboards = dashboardsResponse.data
                        if (dashboards.isEmpty()) {
                            flowOf(Response.Success(emptyList<DashboardDetail>()))
                        } else {
                            val detailFlows = dashboards.map { dashboard ->
                                dashboardUseCase.GetDashboardDetail(dashboard.dashboardId)
                            }
                            combine(detailFlows) { detailsResponses ->
                                val error = detailsResponses.filterIsInstance<Response.Error>().firstOrNull()
                                if (error != null) {
                                    Response.Error(error.message)
                                } else if (detailsResponses.any { it is Response.Loading }) {
                                    Response.Loading
                                } else {
                                    val details = detailsResponses.filterIsInstance<Response.Success<DashboardDetail>>().map { it.data }
                                    Response.Success(details)
                                }
                            }
                        }
                    }
                }
            }

            combine(
                dashboardsDetailsFlow,
                accountUseCase.GetAccounts(),
                transactionRepository.transactions,
                _selectedRange
            ) { detailsResponse, accountsResponse, transactionsResponse, selectedRange ->
                when {
                    detailsResponse is Response.Error ->
                        IncomeVsExpensesSummaryScreenState.Error(detailsResponse.message)
                    accountsResponse is Response.Error ->
                        IncomeVsExpensesSummaryScreenState.Error(accountsResponse.message)
                    transactionsResponse is Response.Error ->
                        IncomeVsExpensesSummaryScreenState.Error(transactionsResponse.message)
                    detailsResponse is Response.Success && accountsResponse is Response.Success && transactionsResponse is Response.Success -> {
                        val dashboards = detailsResponse.data
                        val dashboardTransactions = dashboards
                            .flatMap { it.transactions }
                            .distinctBy { it.transactionId }

                        if (accountsResponse.data.isEmpty()) {
                            IncomeVsExpensesSummaryScreenState.Loaded(
                                monthlyData = emptyList(),
                                totalIncome = 0.0,
                                totalExpenses = 0.0,
                                netIncome = 0.0,
                                savingsRate = 0.0,
                                averageMonthlyIncome = 0.0,
                                averageMonthlyExpenses = 0.0,
                                averageMonthlyNet = 0.0,
                                selectedRange = selectedRange
                            )
                        } else {
                            val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
                            val monthlyData = calculateIncomeVsExpensesData(
                                transactions = dashboardTransactions,
                                dashboards = dashboards,
                                today = today,
                                monthsCount = selectedRange.months
                            )

                            val totalIncome = monthlyData.sumOf { it.income }
                            val totalExpenses = monthlyData.sumOf { it.expenses }
                            val netIncome = totalIncome - totalExpenses
                            val savingsRate = if (totalIncome > 0) ((netIncome / totalIncome) * 100.0).coerceIn(-1000.0, 100.0) else 0.0
                            val monthsCount = monthlyData.size.coerceAtLeast(1)
                            val averageMonthlyIncome = totalIncome / monthsCount
                            val averageMonthlyExpenses = totalExpenses / monthsCount
                            val averageMonthlyNet = netIncome / monthsCount

                            IncomeVsExpensesSummaryScreenState.Loaded(
                                monthlyData = monthlyData,
                                totalIncome = totalIncome,
                                totalExpenses = totalExpenses,
                                netIncome = netIncome,
                                savingsRate = savingsRate,
                                averageMonthlyIncome = averageMonthlyIncome,
                                averageMonthlyExpenses = averageMonthlyExpenses,
                                averageMonthlyNet = averageMonthlyNet,
                                selectedRange = selectedRange
                            )
                        }
                    }
                    else -> IncomeVsExpensesSummaryScreenState.Loading
                }
            }.collect { newState ->
                _state.value = newState
            }
        }
    }
}
